import React from 'react';
import { Box, Typography } from '@mui/material';
import { DOCUMENTS_ALIAS } from 'domain/shared/employee';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { getCountryEmoji } from 'framework/form';
import { IdentityDocument } from './types';

interface DocumentItemProps {
  document: IdentityDocument;
  formatDate: (date: string) => string;
}

const DocumentItem: React.FC<DocumentItemProps> = ({ document, formatDate }) => {
  const isPesel = document.type === DOCUMENTS_ALIAS.PESEL;
  const isNip = document.type === DOCUMENTS_ALIAS.NIP;
  const isRegon = document.type === DOCUMENTS_ALIAS.REGON;

  const documentNumber = (() => {
    if (isPesel || isNip || isRegon) {
      return document.number || '';
    }
    return `${document.documentDetails?.series || ''} ${document.number || ''}`.trim();
  })();

  const showDocumentDetails = !isPesel && !isNip && !isRegon;
  const showScans = !isPesel && !isNip && !isRegon;
  const showCitizenship = isPesel || showDocumentDetails;
  const showCountryOfRegistration = isNip;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle1">
          {document.type} {documentNumber}
        </Typography>

        {(showCitizenship || showDocumentDetails) && (
          <Box sx={{ ml: 'auto' }} fontSize={16}>
            {getCountryEmoji(document.documentDetails?.citizenship)}
          </Box>
        )}

        {showCountryOfRegistration && (
          <Box sx={{ ml: 'auto' }} fontSize={16}>
            {getCountryEmoji(document.documentDetails?.countryOfRegistration)}
          </Box>
        )}
      </Box>

      {showDocumentDetails && (
        <>
          {(document.documentDetails?.issuedAt || document.documentDetails?.issuedBy) && (
            <Box display="flex" alignItems="center" flexWrap="wrap" mb={1}>
              {document.documentDetails?.issuedAt && (
                <>
                  <Typography variant="body1" color="textPrimary" component="span" mr={1}>
                    Issued
                  </Typography>
                  <Typography variant="body1" color="textSecondary" component="span" mr={1}>
                    {formatDate(document.documentDetails.issuedAt)}
                  </Typography>
                </>
              )}
              {document.documentDetails?.issuedBy && (
                <>
                  {document.documentDetails?.issuedAt && (
                    <Typography variant="body1" color="textPrimary" component="span" mr={1}>
                      by
                    </Typography>
                  )}
                  <Typography variant="body1" color="textSecondary" component="span">
                    {document.documentDetails.issuedBy}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {document.expiredAt && (
            <Box display="flex" alignItems="center" flexWrap="wrap" mb={1}>
              <Typography variant="body1" color="textPrimary" component="span" mr={1}>
                Expires on
              </Typography>
              <Typography variant="body1" color="textSecondary" component="span">
                {formatDate(document.expiredAt)}
              </Typography>
            </Box>
          )}

          {document.documentDetails?.personalIdNumber && (
            <Box display="flex" alignItems="center" flexWrap="wrap" mb={1}>
              <Typography variant="body1" color="textPrimary" component="span" mr={1}>
                ID
              </Typography>
              <Typography variant="body1" color="textSecondary" component="span">
                {document.documentDetails.personalIdNumber}
              </Typography>
            </Box>
          )}

          {(document.documentDetails?.givenName || document.documentDetails?.surname) && (
            <Box mt={1}>
              <Typography variant="subtitle1">
                {document.documentDetails?.givenName} {document.documentDetails?.surname}
              </Typography>
            </Box>
          )}

          {showScans && document.scansIds && document.scansIds.length > 0 && (
            <Box mt={2} sx={{ maxWidth: '300px' }}>
              <AttachmentsListField value={document.scansIds} isVisible={true} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DocumentItem;
